import { NgStyle } from '@angular/common'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable } from 'rxjs'

import { DamagedUtility, ListItem, RepositoryModal } from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'

@Component({
    templateUrl: './damaged-utility.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle, VoiceTextFieldComponent],
})
export class DamagedUtilityComponent extends RepositoryModal<DamagedUtility> {
    faX = faX

    jobList: ListItem<string>[] = []
    userList: ListItem<string>[] = []

    // Shared Yes/No/N/A answer set for notification + assessment questions.
    readonly answerOptions: ListItem<string>[] = [
        new ListItem('Yes', 'Yes'),
        new ListItem('No', 'No'),
        new ListItem('N/A', 'N/A'),
    ]

    images: any[] = []
    progress = 0

    protected onInitialized(): void {
        const params = this.editOptions.initializeParams as {
            component: {
                activeJobList: ListItem<string>[]
                activeUserList: ListItem<string>[]
                currentUserId: string
            }
        }
        const parent = params.component
        this.jobList = parent?.activeJobList ?? []
        this.userList = parent?.activeUserList ?? []

        if (this.isAdding) {
            this.target.job ??= ''
            this.target.location ??= ''
            this.target.descriptionOfDamage ??= ''
            this.target.blueStakeNumber ??= ''
            this.target.typeOfUtility ??= ''
            this.target.utilityOwner ??= ''
            this.target.whoMarkedTheUtility ??= ''
            this.target.whoRepairedTheUtility ??= ''
            this.target.damagedBeforeExcavationComments ??= ''
            this.target.vanConAtFaultComments ??= ''
            this.target.withinTwoFootToleranceComments ??= ''
            this.target.markedCorrectlyComments ??= ''
            this.target.archived = false
            this.target.imageUrls = []
            this.target.imageFileNames = []
            if (!this.target.signedByUserId) {
                this.target.signedByUserId = parent?.currentUserId ?? null
            }
        }

        if (this.isDuplicating) {
            this.target.imageUrls = []
            this.target.imageFileNames = []
        }

        this.target.imageUrls ??= []
        this.target.imageFileNames ??= []
    }

    removeImage(image: any) {
        this.images.remove(image)
    }

    async onFileChanged(event: Event) {
        const files = (event.target as HTMLInputElement).files
        if (!files) return
        const progress = (p: number) => {
            this.progress = p
        }
        for (const file of Array.from(files)) {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: progress,
            }
            const compressedFile = await imageCompression(file, options)
            compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
            this.images.push(compressedFile)
        }
        ;(event.target as HTMLInputElement).value = ''
    }

    private uploadImages(id: string): Observable<any> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.images.length; i++) {
            uploadData.append('myFile' + i, this.images[i], this.images[i].name)
        }
        return this.services.data.post('api/DamagedUtility/UploadImages', uploadData)
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.images.length === 0) {
                    resolveSave(successResult)
                    return
                }

                this.uploadImages(successResult.id).subscribe({
                    next: () => resolveSave(successResult),
                    error: errorResult => rejectSave(errorResult),
                })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }
}
